/**
 * SmartRecommendation — Compare possible responses to a supply gap.
 *
 * For each replenishment suggestion we render up to four option cards
 * side-by-side (standard, expedited, transfer, split). Each card shows
 * lead time, est. cost, stockout risk after, trade-off. Selecting a
 * card and approving it marks the suggestion approved in the shared
 * demo state so Commercial Impact + Supply Chain highlights update.
 */
import React from 'react';
import { Card } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Badge } from '../../../../components/ui/badge';
import {
    Factory, Zap, Truck, Split, Check, Clock, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import {
    useDistributionDemo,
    computeInsightStatus,
} from '../DistributionDemoContext';
import InsightStatusBadge from './InsightStatusBadge';

const ICON_MAP = { factory: Factory, zap: Zap, truck: Truck, split: Split };

const RISK_META = {
    very_low: { label: 'Very low', chip: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    low:      { label: 'Low',      chip: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    medium:   { label: 'Medium',   chip: 'bg-amber-100 text-amber-700 border-amber-200' },
    high:     { label: 'High',     chip: 'bg-rose-100 text-rose-700 border-rose-200' },
};

const formatEur = (n) => `€${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function SmartRecommendation({ suggestion, options = [], projection }) {
    const {
        selectedStrategy, setSelectedStrategy,
        approvedReplenishments, setApprovedReplenishments,
        setLeadTimeOverride, setWarehouseTransfer,
        flashNodes,
    } = useDistributionDemo();

    const approved = !!approvedReplenishments[suggestion.suggestion_id];
    const selectedOptionId = selectedStrategy[suggestion.suggestion_id];
    const selectedOption = options.find(o => o.option_id === selectedOptionId);

    const liveStatus = computeInsightStatus(
        { impact_type: 'revenue_at_risk', sku: suggestion.sku },
        {
            projection,
            acknowledged: false,
            warehouseTransfer: null, // handled separately below
            leadTimeOverride: null,
            selectedStrategyForSku: !!selectedOptionId,
            approvedForSku: approved,
            hasOptions: options.length > 0,
        },
    );

    const totalFor = (option) => {
        const qty = Math.min(suggestion.suggested_qty, option.qty_cap ?? suggestion.suggested_qty);
        return qty * option.unit_cost_eur;
    };

    const applyEffect = (option) => {
        // Translate the chosen strategy into measurable simulation state.
        // Standard mfr: no sim effect (gap persists until the real PO lands).
        // Expedited: shortens lead time → may close the gap.
        // Transfer: adds stock from Nordisk → may close the gap.
        // Split: no sim effect (ships on-hand partial, backorder balance).
        if (!option) return;
        if (option.option_id.includes('EXPEDITED')) {
            setLeadTimeOverride(option.lead_time_days);
        } else if (option.option_id.includes('TRANSFER')) {
            const qty = Math.min(suggestion.suggested_qty, option.qty_cap || suggestion.suggested_qty);
            setWarehouseTransfer({ sku: suggestion.sku, qty, from: 'nordisk-no', to: 'gls-se' });
        }
    };

    const approve = () => {
        if (!selectedOption) return;
        applyEffect(selectedOption);
        setApprovedReplenishments(prev => ({ ...prev, [suggestion.suggestion_id]: true }));
        flashNodes(['manufacturer', 'warehouse', 'retailers']);
        toast.success('Replenishment approved — scenario updated (no real order sent)');
    };

    const undo = () => {
        setApprovedReplenishments(prev => {
            const next = { ...prev };
            delete next[suggestion.suggestion_id];
            return next;
        });
    };

    return (
        <Card className="border-slate-200" data-testid={`smart-rec-${suggestion.suggestion_id}`}>
            <div className="p-5">
                <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
                    <div>
                        <div className="flex items-center gap-2">
                            <Sparkles className="h-4 w-4 text-indigo-500" />
                            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                                Smart recommendation · {suggestion.sku}
                            </div>
                        </div>
                        <div className="text-base font-semibold text-slate-900 mt-1">
                            {suggestion.trigger}
                        </div>
                        <div className="text-sm text-slate-600 mt-1">
                            {projection && projection.supplyGapDays > 0 && (
                                <>Current simulation shows <strong>{projection.supplyGapDays}-day gap</strong> · {formatEur(projection.revenueAtRisk)} at risk · suggesting {suggestion.suggested_qty.toLocaleString()} units</>
                            )}
                            {projection && projection.supplyGapDays <= 0 && (
                                <>No gap right now — comparing options for the next ordering window</>
                            )}
                        </div>
                    </div>
                    {approved ? (
                        <div className="flex items-center gap-2">
                            <InsightStatusBadge
                                status={liveStatus}
                                testid={`smart-rec-status-${suggestion.suggestion_id}`}
                            />
                            <Button variant="ghost" size="sm" onClick={undo} data-testid={`smart-rec-undo-${suggestion.suggestion_id}`}>
                                Undo
                            </Button>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2">
                            {selectedOptionId && (
                                <InsightStatusBadge
                                    status="action_selected"
                                    testid={`smart-rec-status-${suggestion.suggestion_id}`}
                                />
                            )}
                            <Button
                                onClick={approve}
                                disabled={!selectedOptionId}
                                data-testid={`smart-rec-approve-${suggestion.suggestion_id}`}
                            >
                                <Check className="h-4 w-4 mr-1" />
                                Approve selected
                            </Button>
                        </div>
                    )}
                </div>

                <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-3">
                    {options.map(opt => {
                        const Icon = ICON_MAP[opt.icon] || Factory;
                        const selected = selectedOptionId === opt.option_id;
                        const risk = RISK_META[opt.stockout_risk] || RISK_META.medium;
                        const effectiveQty = Math.min(suggestion.suggested_qty, opt.qty_cap ?? suggestion.suggested_qty);
                        const capped = opt.qty_cap !== null && opt.qty_cap !== undefined && opt.qty_cap < suggestion.suggested_qty;
                        return (
                            <button
                                key={opt.option_id}
                                onClick={() => setSelectedStrategy(prev => ({ ...prev, [suggestion.suggestion_id]: opt.option_id }))}
                                disabled={approved}
                                data-testid={`strategy-${opt.option_id}`}
                                className={`text-left rounded-lg border-2 p-4 transition-all ${
                                    selected
                                        ? 'border-indigo-500 bg-indigo-50/50 shadow-sm'
                                        : 'border-slate-200 bg-white hover:border-slate-300'
                                } ${approved ? 'opacity-60 cursor-default' : 'cursor-pointer'}`}
                            >
                                <div className="flex items-start justify-between mb-2">
                                    <div className={`h-8 w-8 rounded-md flex items-center justify-center ${
                                        selected ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600'
                                    }`}>
                                        <Icon className="h-4 w-4" />
                                    </div>
                                    <Badge variant="outline" className={`text-[10px] uppercase tracking-wider border ${risk.chip}`}>
                                        Risk · {risk.label}
                                    </Badge>
                                </div>

                                <div className="font-semibold text-slate-900 leading-tight">{opt.label}</div>
                                <div className="text-xs text-slate-500 mt-0.5">{opt.partner}</div>

                                <div className="mt-3 space-y-1.5 text-xs">
                                    <div className="flex items-center justify-between">
                                        <span className="text-slate-500 flex items-center gap-1">
                                            <Clock className="h-3 w-3" />
                                            Lead time
                                        </span>
                                        <span className="font-semibold text-slate-900">
                                            {opt.lead_time_days === 0 ? 'Immediate (partial)' : `${opt.lead_time_days}d`}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-slate-500">Unit cost</span>
                                        <span className="font-semibold text-slate-900">€{opt.unit_cost_eur.toFixed(2)}</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-slate-500">
                                            {capped ? 'Qty (capped)' : 'Est. total'}
                                        </span>
                                        <span className="font-semibold text-slate-900 tabular-nums">
                                            {capped ? `${effectiveQty.toLocaleString()} units` : formatEur(totalFor(opt))}
                                        </span>
                                    </div>
                                </div>

                                <div className="mt-3 text-[11px] text-slate-600 leading-relaxed">
                                    {opt.trade_off}
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        </Card>
    );
}
